/* eslint-disable @typescript-eslint/no-explicit-any */
// ** Auth Store - store/slices/authSlice.ts
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import config from "@src/@core/auth/jwt/jwtDefaultConfig";
import { authService } from "@src/@api-services/auth";
import {
  extractProfileFromApiPayload,
  mergeAuthUser,
  type MergedAuthUser,
} from "@src/utility/userProfile";
import { syncAbility } from "@src/configs/acl/ability";
import {
  matchUserPermissions,
  normalizePermissionIds,
  readStoredPermissionCatalog,
  storePermissionCatalog,
  type IPermissionCatalogItem,
} from "@src/utility/permissions/permissionUtils";

// ** Types
interface IProfile {
  id?: string | number;
  firstName: string;
  lastName: string;
  email: string;
  profileImage?: string;
  phoneNumber?: string;
  accessToken?: string;
  refreshToken?: string;
  role?: string;
  roleName?: string;
  fullName?: string;
  username?: string;
  ability?: any;
  [key: string]: any;
}

interface LoginCredentials {
  email: string;
  password: string;
}

interface ForgotPasswordData {
  email: string;
}

interface ResetPasswordData {
  password: string;
  confirmPassword: string;
  token?: string;
}

interface ChangePasswordData {
  currentPassword: string;
  newPassword: string;
  confirmPassword: string;
}

interface AuthState {
  user: IProfile | null;
  token: string | null;
  isAuthenticated: boolean;
  loading: boolean;
  error: string | null;
  // 2FA fields
  requires2FA: boolean;
  userId: number | null;
  permissionCatalog: IPermissionCatalogItem[];
  userPermissionIds: number[];
  userPermissions: IPermissionCatalogItem[];
  permissionCatalogLoading: boolean;
}

interface Verify2FAData {
  userId: number;
  code: string;
}

interface ResendOtpData {
  email: string;
}

const toAuthProfile = (user: MergedAuthUser): IProfile => user as IProfile;

/** Stored user profile only — never read permissions from JWT. */
const readStoredPermissionIds = (): number[] => {
  const storedUser = authService.getUserData();
  if (!Array.isArray(storedUser?.permissionIds)) return [];
  return normalizePermissionIds(storedUser.permissionIds);
};

const syncUserPermissions = (
  catalog: IPermissionCatalogItem[],
  permissionIds: number[]
) => {
  const normalizedIds = normalizePermissionIds(permissionIds);
  return {
    userPermissionIds: normalizedIds,
    userPermissions: matchUserPermissions(catalog, normalizedIds),
  };
};

const persistUserPermissionIds = (
  state: AuthState,
  permissionIds: number[]
) => {
  Object.assign(state, syncUserPermissions(state.permissionCatalog, permissionIds));

  if (!state.user) return;

  const updatedUser = { ...state.user, permissionIds };
  state.user = updatedUser;
  const remember = !!localStorage.getItem(config.storageTokenKeyName);
  authService.setUserData(updatedUser, remember);
};

// ** Async Thunks using authService
export const loginUser = createAsyncThunk(
  "auth/loginUser",
  async (credentials: LoginCredentials, { rejectWithValue }) => {
    try {
      const response = await authService.login(credentials);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Something went wrong."
      );
    }
  }
);

export const logoutUser = createAsyncThunk(
  "auth/logoutUser",
  async (_, { rejectWithValue }) => {
    try {
      await authService.logout();
      // Use authService method for complete cleanup
      authService.clearSession();
      return null;
    } catch (error: any) {
      // Even if API fails, clear session locally
      authService.clearSession();
      return rejectWithValue(error.response?.data?.message || "Logout failed");
    }
  }
);

export const getUserProfile = createAsyncThunk(
  "auth/getUserProfile",
  async (_, { rejectWithValue }) => {
    try {
      const response = await authService.getProfile();
      return response.data;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch profile"
      );
    }
  }
);

export const forgotPassword = createAsyncThunk(
  "auth/forgotPassword",
  async (data: ForgotPasswordData, { rejectWithValue }) => {
    try {
      const response = await authService.forgotPassword(data.email);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to send reset email"
      );
    }
  }
);

export const resetPassword = createAsyncThunk(
  "auth/resetPassword",
  async (data: ResetPasswordData, { rejectWithValue }) => {
    try {
      const response = await authService.resetPassword(data);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to reset password"
      );
    }
  }
);

export const changePassword = createAsyncThunk(
  "auth/changePassword",
  async (data: ChangePasswordData, { rejectWithValue }) => {
    try {
      const response = await authService.changePassword(data);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to change password"
      );
    }
  }
);

export const updateProfile = createAsyncThunk(
  "auth/updateProfile",
  async (profileData: Partial<IProfile>, { rejectWithValue }) => {
    try {
      console.log("🟡 Updating user profile...");
      const response = await authService.updateProfile(profileData);
      console.log("🟢 Profile updated successfully");
      return response.data;
    } catch (error: any) {
      console.log("🔴 Profile update failed:", error);
      return rejectWithValue(
        error.response?.data?.message || "Failed to update profile"
      );
    }
  }
);

export const verify2FA = createAsyncThunk(
  "auth/verify2FA",
  async (data: Verify2FAData, { rejectWithValue }) => {
    try {
      const response = await authService.verify2FA(data);
      const result = response.data?.data || response.data;
      const token = result?.token;

      if (!token) {
        return rejectWithValue("Invalid verification response");
      }

      authService.setToken(token, true);

      const permissionIds = await authService.fetchMyPermissionIds();

      return { result, permissionIds };
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Invalid verification code"
      );
    }
  }
);

export const fetchPermissionCatalog = createAsyncThunk(
  "auth/fetchPermissionCatalog",
  async (_, { rejectWithValue }) => {
    try {
      const response = await authService.getPermissionCatalog();
      const catalog = response?.data?.data;
      return Array.isArray(catalog) ? catalog : [];
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch permission catalog"
      );
    }
  }
);

export const refreshUserPermissions = createAsyncThunk(
  "auth/refreshUserPermissions",
  async (_, { rejectWithValue }) => {
    try {
      return await authService.fetchMyPermissionIds();
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to refresh permissions"
      );
    }
  }
);

export const resendOtp = createAsyncThunk(
  "auth/resend-otp",
  async (data: ResendOtpData, { rejectWithValue }) => {
    try {
      const response = await authService.resendOtp(data);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Invalid verification code"
      );
    }
  }
);

// ** Initial State - Use authService methods
const storedPermissionIds = readStoredPermissionIds();
const storedPermissionCatalog = readStoredPermissionCatalog();
const initialUserPermissions = matchUserPermissions(
  storedPermissionCatalog,
  storedPermissionIds
);

const initialState: AuthState = {
  user: authService.getUserData(),
  token: authService.getToken(),
  isAuthenticated: authService.isAuthenticated(),
  loading: false,
  error: null,
  requires2FA: false,
  userId: null,
  permissionCatalog: storedPermissionCatalog,
  userPermissionIds: storedPermissionIds,
  userPermissions: initialUserPermissions,
  permissionCatalogLoading: false,
};

// ** Slice
export const authSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    clearAuth: (state) => {
      state.user = null;
      state.token = null;
      state.isAuthenticated = false;
      state.requires2FA = false;
      state.userId = null;
      state.userPermissionIds = [];
      state.userPermissions = [];
      
      // Use authService for complete cleanup
      authService.clearSession();
      syncAbility(null);
    },
    // ** Set 2FA requirement (for when login requires 2FA)
    set2FARequired: (state, action) => {
      state.requires2FA = true;
      state.userId = action.payload.userId;
      state.loading = false;
    },
  },
  extraReducers: (builder) => {
    builder
      // Login User
      .addCase(loginUser.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(loginUser.fulfilled, (state, action) => {
        state.loading = false;
        const result = action.payload;

        // ** Check if 2FA is required
        if (result.data?.requires2FA) {
          console.log("🟡 2FA required for user:", result.data.userId);
          state.requires2FA = true;
          state.userId = result.data.userId;
          state.error = null;
        } else {
          // ** Normal login successful
          console.log("🟢 Login successful, setting user data");
          
          const userData = result.data || result;
          
          // Create complete user object with all necessary fields
          const userObj = mergeAuthUser(null, {
            id: userData.id || userData.user?.id,
            firstName: userData.firstName || userData.user?.firstName || '',
            lastName: userData.lastName || userData.user?.lastName || '',
            email: userData.email || userData.user?.email || '',
            profileImage: userData.profileImage || userData.user?.profileImage || '',
            phoneNumber: userData.phoneNumber || userData.user?.phoneNumber || '',
            accessToken: userData.token || userData.accessToken,
            refreshToken: userData.refreshToken,
            role: userData.role || userData.user?.role,
            roleName: userData.roleName || userData.user?.roleName,
            username: userData.username || userData.user?.username,
            ability: userData.ability || userData.user?.ability,
          });
          
          const token = userData.token || userData.accessToken;
          const refreshToken = userData.refreshToken;
          
          // Update state
          state.user = toAuthProfile(userObj);
          state.token = token;
          state.isAuthenticated = true;
          state.requires2FA = false;
          state.userId = null;
          state.error = null;
          
          // Use authService to store session (remember = true for localStorage)
          authService.initializeSession(token, toAuthProfile(userObj), refreshToken, true);
          syncAbility(userObj.ability as Parameters<typeof syncAbility>[0]);
          
          console.log("🟢 User session initialized successfully");
        }
      })
      .addCase(loginUser.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
        state.isAuthenticated = false;
      })

      // Logout User
      .addCase(logoutUser.fulfilled, (state) => {
        state.user = null;
        state.token = null;
        state.isAuthenticated = false;
        state.requires2FA = false;
        state.userId = null;
        state.userPermissionIds = [];
        state.userPermissions = [];
        syncAbility(null);
        
        // authService.clearSession() already called in thunk
        console.log("🔴 Logout completed");
      })
      
      // Verify 2FA
      .addCase(verify2FA.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(verify2FA.fulfilled, (state, action) => {
        state.loading = false;
        const { result, permissionIds } = action.payload;

        const userObj = mergeAuthUser(null, {
          id: result.user?.userId ?? result.user?.id,
          firstName: result.user?.firstName || '',
          lastName: result.user?.lastName || '',
          email: result.user?.email || '',
          profileImage: result.user?.profileImage || '',
          phoneNumber: result.user?.phoneNumber || '',
          accessToken: result.token,
          refreshToken: result.refreshToken,
          role: result.user?.role,
          roleName: result.user?.roleName,
          username: result.user?.username,
          ability: result.user?.ability,
          permissionIds,
        });
        
        state.user = toAuthProfile(userObj);
        state.token = result.token;
        state.isAuthenticated = true;
        state.requires2FA = false;
        state.userId = null;
        persistUserPermissionIds(state, permissionIds);
        
        authService.initializeSession(
          result.token,
          toAuthProfile(userObj),
          result.refreshToken,
          true
        );
        syncAbility(userObj.ability as Parameters<typeof syncAbility>[0]);
      })
      .addCase(verify2FA.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })

      // Get User Profile — background refresh; do not block app shell
      .addCase(getUserProfile.pending, (state) => {
        state.error = null;
      })
      .addCase(getUserProfile.fulfilled, (state, action) => {
        const profileData = extractProfileFromApiPayload(action.payload);
        const updatedUser = toAuthProfile(mergeAuthUser(state.user, profileData));

        state.user = updatedUser;
        state.isAuthenticated = true;
        authService.setUserData(updatedUser, true);
        if (updatedUser.ability) {
          syncAbility(updatedUser.ability as Parameters<typeof syncAbility>[0]);
        }
      })
      .addCase(getUserProfile.rejected, (state, action) => {
        state.error = action.payload as string;
      })

      // Update Profile
      .addCase(updateProfile.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateProfile.fulfilled, (state, action) => {
        state.loading = false;
        const profileData = extractProfileFromApiPayload(action.payload);
        const updatedUser = toAuthProfile(mergeAuthUser(state.user, profileData));

        console.log("🟢 Profile update successful, updating state:", updatedUser);

        state.user = updatedUser;
        authService.setUserData(updatedUser, true);
        state.error = null;
      })
      .addCase(updateProfile.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })

      // Change Password
      .addCase(changePassword.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(changePassword.fulfilled, (state) => {
        state.loading = false;
        state.error = null;
      })
      .addCase(changePassword.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })

      // Permission catalog (public)
      .addCase(fetchPermissionCatalog.pending, (state) => {
        state.permissionCatalogLoading = true;
      })
      .addCase(fetchPermissionCatalog.fulfilled, (state, action) => {
        state.permissionCatalogLoading = false;
        state.permissionCatalog = action.payload;
        storePermissionCatalog(action.payload);

        const permissionIds =
          state.userPermissionIds.length > 0
            ? state.userPermissionIds
            : readStoredPermissionIds();

        Object.assign(state, syncUserPermissions(action.payload, permissionIds));
      })
      .addCase(fetchPermissionCatalog.rejected, (state) => {
        state.permissionCatalogLoading = false;
      })

      .addCase(refreshUserPermissions.fulfilled, (state, action) => {
        persistUserPermissionIds(state, action.payload as number[]);
      });
  },
});

// ** Export Actions
export const { clearError, clearAuth, set2FARequired } = authSlice.actions;

// ** Default Export
export default authSlice.reducer;